import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    MenuItem,
    IconButton,
    Chip,
    InputAdornment,
} from '@mui/material';
import { Add, ArrowBackIosNew } from '@mui/icons-material';
import ColorPicker from 'components/ColorPicker';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const WORK_MODES = ['SHIFT', 'OFFICE', 'OT', 'LEAVE'];

const schema = yup.object().shape({
    shiftCode:         yup.string().required('Required'),
    startTime:         yup.string().required('Required').matches(/^\d{2}:\d{2}$/, 'Format HH:MM'),
    endTime:           yup.string().required('Required').matches(/^\d{2}:\d{2}$/, 'Format HH:MM'),
    workMode:          yup.string().oneOf(WORK_MODES).required('Required'),
    workingTime:       yup.number().min(0),
    paidFactor:        yup.number().min(0),
    requiredEmployees: yup.number().min(0).integer(),
});

const INITIAL = {
    shiftCode:         '',
    shiftName:         '',
    startTime:         '08:00',
    endTime:           '17:00',
    crossesDay:        false,
    workMode:          'SHIFT',
    workingTime:       8,
    paidFactor:        1,
    requiredEmployees: 1,
    requiredSkills:    [],
    color:             '',
    isActive:          true,
};


const ShiftPage = () => {
    useAuthCheck();
    const { shiftId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);
    const [shift, setShift]     = useState({});
    const [skillInput, setSkillInput] = useState('');
    const [colorAnchor, setColorAnchor] = useState(null);

    useEffect(() => {
        if (!shiftId || shiftId === 'new') return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/shifts/${shiftId}`);
                setShift(data);
            } catch (err) {
                setError(err);
                toast.error(err?.response?.data?.message || 'Failed to fetch shift!');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [shiftId]);

    const initialValues = {
        ...INITIAL,
        ...shift,
        requiredSkills: shift.requiredSkills || [],
    };

    const handleSubmit = async (values) => {
        try {
            const isEdit = shiftId && shiftId !== 'new';
            if (isEdit) {
                await api.put(`/shifts/update/${shiftId}`, values);
                toast.success('Shift updated successfully.');
            } else {
                await api.post('/shifts/new', values);
                toast.success('Shift created successfully.');
            }
            navigate('/shifts');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Failed to save shift!');
        }
    };

    if (loading) return <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>;
    if (error)   return <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">{error?.message}</Typography></FlexCenter>;

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Shift"
                    subtitle={shiftId && shiftId !== 'new' ? 'Update' : 'Create'}
                />
                <StyledButton onClick={() => navigate('/shifts')}>
                    <ArrowBackIosNew />
                </StyledButton>
            </FlexBetween>

            <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={schema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fSubmit, setFieldValue }) => (
                        <form onSubmit={fSubmit}>
                            <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap={2}>
                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    label="Shift Code"
                                    name="shiftCode"
                                    value={values.shiftCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!touched.shiftCode && !!errors.shiftCode}
                                    helperText={touched.shiftCode && errors.shiftCode}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                    slotProps={{ htmlInput: { style: { textTransform: 'uppercase' } } }}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    label="Shift Name"
                                    name="shiftName"
                                    value={values.shiftName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    label="Work Mode"
                                    select
                                    name="workMode"
                                    value={values.workMode}
                                    onChange={handleChange}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 12' } }}
                                >
                                    {WORK_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                </TextField>
                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    label="Start Time"
                                    name="startTime"
                                    value={values.startTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="HH:MM"
                                    error={!!touched.startTime && !!errors.startTime}
                                    helperText={(touched.startTime && errors.startTime) || 'HH:MM (24h)'}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />
                                <TextField
                                    required
                                    fullWidth
                                    variant="filled"
                                    label="End Time"
                                    name="endTime"
                                    value={values.endTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="HH:MM"
                                    error={!!touched.endTime && !!errors.endTime}
                                    helperText={(touched.endTime && errors.endTime) || 'HH:MM (24h)'}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="number"
                                    label="Working Hours"
                                    name="workingTime"
                                    value={values.workingTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!touched.workingTime && !!errors.workingTime}
                                    helperText={touched.workingTime && errors.workingTime}
                                    slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="number"
                                    label="Paid Factor"
                                    name="paidFactor"
                                    value={values.paidFactor}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!touched.paidFactor && !!errors.paidFactor}
                                    helperText={touched.paidFactor && errors.paidFactor}
                                    slotProps={{ htmlInput: { min: 0, step: 0.25 } }}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />

                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="number"
                                    label="Required Employees"
                                    name="requiredEmployees"
                                    value={values.requiredEmployees}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    slotProps={{ htmlInput: { min: 0 } }}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />
                                
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    label="Color"
                                    name="color"
                                    value={values.color}
                                    onChange={handleChange}
                                    onClick={e => setColorAnchor(e.currentTarget)}
                                    placeholder="#3B82F6"
                                    slotProps={{
                                        htmlInput: { style: { cursor: 'pointer' } },
                                        input: {
                                            startAdornment: values.color ? (
                                                <InputAdornment position="start">
                                                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: values.color, border: '1px solid', borderColor: 'divider' }} />
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                    sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                                />
                                <ColorPicker
                                    anchorEl={colorAnchor}
                                    onClose={() => setColorAnchor(null)}
                                    value={values.color}
                                    onChange={c => setFieldValue('color', c)}
                                />

                                {/* Required Skills */}
                                <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 12' } }}>
                                    <Typography variant="caption" color="text.secondary" mb={1} display="block">
                                        Required Skills
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            placeholder="Add skill..."
                                            value={skillInput}
                                            onChange={e => setSkillInput(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && skillInput.trim()) {
                                                    e.preventDefault();
                                                    setFieldValue('requiredSkills', [...values.requiredSkills, skillInput.trim()]);
                                                    setSkillInput('');
                                                }
                                            }}
                                            sx={{ flex: 1 }}
                                        />
                                        <IconButton
                                            onClick={() => {
                                                if (!skillInput.trim()) return;
                                                setFieldValue('requiredSkills', [...values.requiredSkills, skillInput.trim()]);
                                                setSkillInput('');
                                            }}
                                        >
                                            <Add />
                                        </IconButton>
                                    </Box>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {values.requiredSkills.map((skill, i) => (
                                            <Chip
                                                key={i}
                                                label={skill}
                                                size="small"
                                                onDelete={() => setFieldValue('requiredSkills', values.requiredSkills.filter((_, idx) => idx !== i))}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Checkboxes */}
                                <Box sx={{ gridColumn: 'span 12', display: 'flex', gap: 3, alignItems: 'center' }}>
                                    <FormControlLabel
                                        label="Crosses Day (+1)"
                                        control={<Checkbox name="crossesDay" checked={values.crossesDay} onChange={handleChange} />}
                                    />
                                    <FormControlLabel
                                        label="Is Active"
                                        control={<Checkbox name="isActive" checked={values.isActive} onChange={handleChange} />}
                                    />
                                </Box>

                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ gridColumn: 'span 12', height: 42 }}
                                >
                                    {shiftId && shiftId !== 'new' ? 'Update' : 'Create'}
                                </Button>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default ShiftPage;
